/**
 * Einen angefangenen Anruf über den Wechsel in die Telefon-App retten.
 *
 * Auf dem Handy verlässt der Browser beim Wählen die Seite. Kommt sie neu
 * geladen zurück — bei knappem Speicher die Regel, nicht die Ausnahme —, ist der
 * offene Ergebnis-Dialog weg. Genau die Anrufe, bei denen niemand rangeht und
 * die deshalb kurz sind, wären damit die am seltensten dokumentierten: der Fall,
 * für den das Protokoll überhaupt gebaut ist.
 *
 * Deshalb wird der Anruf VOR dem Wählen lokal vermerkt und nach der Rückkehr
 * wieder aufgemacht. Rein im Browser des Technikers, ohne Server.
 */
import type { CallOriginType } from '../../lib/api';
import type { CallTargetTicket } from './CallButton';

const STORAGE_KEY = 'portal.pendingCall';

/**
 * Nach dieser Zeit gilt ein vermerkter Anruf als erledigt.
 *
 * Wer zwei Stunden später zurückkommt, weiß nicht mehr, worum es ging — ein
 * Dialog, der dann noch aufgeht, produziert Fantasieeinträge statt Protokoll.
 */
const MAX_AGE_MS = 30 * 60_000;

export interface PendingCall {
  /** ISO-Zeitstempel des Klicks auf „Anrufen". */
  startedAt: string;
  phone: string;
  contactName?: string | null;
  customerId?: string | null;
  contactId?: string | null;
  ticket?: CallTargetTicket | null;
  originType?: CallOriginType;
  originId?: string | null;
  /**
   * Fingerabdruck der auslösenden Stelle. Auf einer Seite stehen viele
   * Anruf-Knöpfe; ohne ihn würden nach dem Neuladen alle gleichzeitig den
   * Dialog aufmachen.
   */
  key: string;
}

/** Innerhalb eines Seitenaufbaus darf ein vermerkter Anruf nur EINMAL beansprucht
 *  werden — sonst öffnen zwei gleichartige Knöpfe denselben Dialog doppelt. */
const claimed = new Set<string>();

export function pendingCallKey(parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join('|');
}

export function rememberPendingCall(call: PendingCall): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(call));
  } catch {
    // Privater Modus oder volle Ablage: Der Anruf geht trotzdem raus, nur die
    // Rettung nach dem Neuladen entfällt. Kein Grund, das Wählen zu verhindern.
  }
}

export function forgetPendingCall(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* siehe oben */
  }
}

/**
 * Gibt den vermerkten Anruf zurück, wenn er zu dieser Stelle gehört und frisch
 * genug ist — und nur beim ersten Fragen.
 */
export function takePendingCall(key: string): PendingCall | null {
  if (claimed.has(key)) return null;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: PendingCall;
  try {
    parsed = JSON.parse(raw) as PendingCall;
  } catch {
    forgetPendingCall();
    return null;
  }

  if (parsed.key !== key) return null;
  const age = Date.now() - Date.parse(parsed.startedAt);
  if (!Number.isFinite(age) || age < 0 || age > MAX_AGE_MS) {
    forgetPendingCall();
    return null;
  }

  claimed.add(key);
  return parsed;
}

/** Wählt und vermerkt den Anruf. Gibt den Startzeitpunkt zurück. */
export function dialNumber(call: Omit<PendingCall, 'startedAt'>): Date {
  // Zeitpunkt VOR dem Wählen: Nach der Rückkehr aus der Telefon-App wäre „jetzt"
  // der Zeitpunkt des Auflegens, nicht der des Anrufs.
  const startedAt = new Date();
  rememberPendingCall({ ...call, startedAt: startedAt.toISOString() });
  window.location.href = `tel:${call.phone.replace(/[^\d+]/g, '')}`;
  return startedAt;
}
